import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    Grid,
    InputLabel,
    IconButton,
    useMediaQuery,
    useTheme,
    Tooltip,
} from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IPagination, usePagination } from '../../../../hook/usePagination';
import CustomButton from '../../../Button/CustomButton';
import { ColumnsData, Table } from '../../../Tables/Table';
import { formatDate, showToastMessage, useDispatchPromise } from '../../../../utils/AppUtils';
import { CustomPagination } from '../../../Tables/Pagination';
import {
    Search as SearchIcon,
    Sort as SortIcon,
    Close as CloseIcon,
    FileDownload as ExportIcon,
    EmojiEvents as AwardIcon,
    VideoLibrary as SelectionIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import _ from 'lodash';
import {
    IManageContestSubmissions,
    TCampSortOrder,
    TCategoriesSummary,
    TSortBy,
} from '../../../../redux/contest/types';
import { useDispatch, useSelector } from 'react-redux';
import { setmedia } from '../../../../redux/project/actions';
import { navigate } from '../../../../redux/navigator/action';
import { replaceRouteParam } from '../../../../utils/routesProcessor';
import { ROUTES } from '../../../../types/global/routes.types';
import { awardPrize, getContestManage, sendWinnersEmail } from '../../../../redux/contest/actions';
import { IContest } from '../../../../types/global/helper';
import { PrizeAwardModal } from './PrizeAwardModal';
import { OfficialSelectionMenu } from './OfficialSelectionMenu';
import { managedCampaignSelector } from '../../../../pages/Contests/selector';
import { DialogueSheet } from '../../../DialogueSheet/DialogueSheet';
import { isEmpty } from '../../../../utils/lodash';
import { getIdForIbjects } from '../../../../utils/AppUtils';

interface IContestSubmissionsTabProps {
    submissions?: IManageContestSubmissions[];
    contestId?: string;
    contest?: IContest;
    awardCategories?: TCategoriesSummary;
    pagination: IPagination;
    sortOrder: 'asc' | 'desc';
    onSearchChange: (searchText: string) => void;
    onStartDateChange: (date: string) => void;
    onEndDateChange: (date: string) => void;
    onSortOrderChange: (order: 'asc' | 'desc') => void;
    onSortByChange: (sortBy: TSortBy) => void;
    onRowsChange: (rows: number) => void;
    onPageChange: (page: number) => void;
    onClearSearch: () => void;
    onClearStartDate: () => void;
    onClearEndDate: () => void;
    onExportCSV: (submissions: any) => void;
}

// Sort options
const SORT_OPTIONS = [
    { value: 'createdAt_desc', label: 'Oldest First' },
    { value: 'createdAt_asc', label: 'Newest First' },
    { value: 'votes_desc', label: 'Most Votes' },
    { value: 'votes_asc', label: 'Least Votes' },
    { value: 'jury_score_desc', label: 'Highest Jury Score' },
    { value: 'jury_score_asc', label: 'Lowest Jury Score' },
];

export const ContestSubmissionsTab: React.FC<IContestSubmissionsTabProps> = ({
    submissions = [],
    contestId,
    contest,
    awardCategories,
    onExportCSV,
    pagination,
}) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [sortOption, setSortOption] = useState('createdAt_desc');
    const [sortOrder, setSortOrder] = useState<TCampSortOrder | undefined>();
    const [sortBy, setSortBy] = useState<TSortBy | undefined>();
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [prizeAwardModalOpen, setPrizeAwardModalOpen] = useState(false);
    const [officialSelectionOpen, setOfficialSelectionOpen] = useState(false);
    const [winnersEmailDialogOpen, setWinnersEmailDialogOpen] = useState(false);
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const { managedContest } = useSelector(managedCampaignSelector);

    // Check if all awards have been assigned (same logic as EsContestSubmission)
    const canSendWinnerEmail = useMemo(() => {
        if (!contest || !awardCategories || !managedContest?.awards) {
            return false;
        }

        // Check if winner email hasn't been sent yet
        const emailNotSent = !contest.winner_email_sent || contest.winner_email_sent === undefined;

        // Check if there are award categories
        const hasAwardCategories = !isEmpty(awardCategories.categories);

        // Check if every award category has at least one award assigned
        const allAwardsAssigned = awardCategories.categories.every(category => {
            // Check if this category has any awards in the managed contest awards
            return managedContest.awards.awards.some(award => {
                if (award) {
                    return getIdForIbjects(award.award_category) === category._id;
                }
                return false;
            });
        });

        return emailNotSent && hasAwardCategories && allAwardsAssigned;
    }, [contest, awardCategories, managedContest?.awards]);

    /** Handle the pagination here */
    // Pagination for submissions data
    const {
        pagination: submissionPagination,
        setPagination: setSubmissionPagination,
        handleRowsChange: handleSubmissionRowsChange,
        handlePageChange: handleSubmissionPageChange,
    } = usePagination(1, 20);

    const handleGetSubmissionData = useCallback(async () => {
        if (contestId) {
            const response: any = await dispatchPromise(
                getContestManage({
                    contestId: contestId,
                    category: 'submissions',
                    searchTerm: debouncedSearchValue,
                    sortOrder,
                    sortBy,
                    submissionPage: submissionPagination.page,
                    submissionLimit: submissionPagination.rows,
                    ...(selectedCategory !== 'all' && { submissionCategory: selectedCategory }),
                }),
            );

            // Update pagination state based on response
            if (response?.data) {
                if (response.data.submissions?.pagination) {
                    const { currentPage, pageSize, totalCount } = response.data.submissions.pagination;
                    setSubmissionPagination({
                        page: currentPage,
                        rows: pageSize,
                        totalCount,
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sortOrder,
        sortBy,
        submissionPagination.page,
        submissionPagination.rows,
        debouncedSearchValue,
        contestId,
        selectedCategory,
    ]);

    useEffect(() => {
        handleGetSubmissionData();
    }, [handleGetSubmissionData]);

    // Create debounced search handler
    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            setDebouncedSearchValue(searchValue);
        }, 500),
    ).current;

    // Debounce and update debouncedSearchValue
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchValue]);

    // Clean up debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearchHandler.cancel();
        };
    }, [debouncedSearchHandler]);

    // Handle search input change
    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setSearchValue(newValue);
        debouncedSearchHandler(newValue);
    };

    // Handle clear search
    const handleClearSearch = () => {
        setSearchValue('');
        setDebouncedSearchValue('');
    };

    // Handle sort change
    const handleSortChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSortOption(value);

        // Extract sort field and order from the value
        const [field, order] = value.split('_');

        // Call the parent's sort handler with the appropriate values
        setSortOrder(order as TCampSortOrder);
        setSortBy(field as TSortBy);
    };

    // Handle category change
    const handleCategoryChange = (event: SelectChangeEvent<string>) => {
        setSelectedCategory(event.target.value);
    };

    // Handle prize award
    const handlePrizeAward = (awards: any[]) => {
        /** Fail Safe. Better to handle this in the BE */
        const totalFunded = contest?.amount_available;
        const awardsTotalFund =
            awardCategories && awardCategories.categories.reduce((total, item) => total + (Number(item.prize) || 0), 0);

        if (awardsTotalFund && totalFunded) {
            if (awardsTotalFund > totalFunded) {
                showToastMessage('Total award amount exceeds your current fund, please fix', 'error');
                return;
            }
        }
        dispatch(awardPrize(awards));
    };

    // Handle send winners email
    const handleSendWinnersEmail = () => {
        setWinnersEmailDialogOpen(true);
    };

    // Handle confirmed send winners email
    const handleConfirmedSendWinnersEmail = (isTest: boolean) => {
        if (contestId) {
            dispatch(
                sendWinnersEmail({
                    contestId: contestId,
                    is_test: isTest,
                }),
            );
        }
        setWinnersEmailDialogOpen(false);
    };

    // Handle cancel winners email
    const handleCancelWinnersEmail = () => {
        setWinnersEmailDialogOpen(false);
    };

    const handleViewSubmission = (submission: IManageContestSubmissions) => {
        dispatch(
            setmedia({
                media: submission.project,
                mediaType: 'act',
            }),
        );
    };

    const handleVisitUser = (submission: IManageContestSubmissions) => {
        const route = replaceRouteParam(ROUTES.ESMY_PROFILE, 'profileId?', submission.created_by.userName);
        dispatch(navigate({ routes: route }));
    };

    const columnsData: ColumnsData[] = [
        {
            field: 'project.feature_image',
            label: 'Project',
            type: 'image' as const,
            width: '15%',
            flex: 1,
            sortable: false,
            onClick: (params: IManageContestSubmissions) => {
                dispatch(
                    setmedia({
                        media: params.project,
                        mediaType: 'act',
                    }),
                );
            },
        },
        {
            field: 'project.title',
            label: 'Title',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '25%',
            flex: 1,
            sortable: true,
            customCell: (params: IManageContestSubmissions) => (
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 12,
                        color: theme[themeStyle].tag_white,
                        cursor: 'pointer',
                        '&:hover': {
                            color: theme[themeStyle].highlight,
                        },
                    }}>
                    {params.project?.title}
                </Typography>
            ),
        },
        {
            field: 'created_by.userName',
            label: 'Submitted By',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '20%',
            flex: 1,
            sortable: true,
            customCell: (params: IManageContestSubmissions) => (
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 12,
                        color: theme[themeStyle].tag_white,
                        cursor: 'pointer',
                        '&:hover': {
                            color: theme[themeStyle].highlight,
                        },
                    }}
                    onClick={() => handleVisitUser(params)}>
                    @{params.created_by?.userName || 'Unknown User'}
                </Typography>
            ),
            onClick: params => {
                handleVisitUser(params);
            },
        },
        {
            field: 'createdAt',
            label: 'Date Submitted',
            type: 'text' as const,
            textType: 'date' as const,
            width: '20%',
            flex: 1,
            sortable: true,
            customCell: (params: IManageContestSubmissions) => (
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 12,
                        color: theme[themeStyle].tag_white,
                    }}>
                    {formatDate(params.createdAt, 'MMM D, YY [at] h:mm A')}
                </Typography>
            ),
        },
        {
            field: 'judging_status',
            label: 'Judging Status',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '20%',
            flex: 1,
            sortable: false,
        },
        {
            field: 'totalVotes',
            label: 'Votes',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '20%',
            flex: 1,
            sortable: true,
        },
        {
            field: 'averageJuryScore',
            label: 'Jury Score',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '20%',
            flex: 1,
            sortable: true,
        },
        {
            field: 'actions',
            label: 'Actions',
            type: 'action' as const,
            width: '5%',
            flex: 1,
            sortable: false,
        },
    ];

    const moreMenuItems = [
        {
            title: 'View Submission',
            id: 'view-submission',
            onClick: handleViewSubmission,
        },
        {
            title: 'Visit User',
            id: 'visit-user',
            onClick: handleVisitUser,
        },
    ];

    const handleOnMenuClicks = (type: string, submission: IManageContestSubmissions) => {
        switch (type) {
            case 'view-submission':
                handleViewSubmission(submission);
                break;
            case 'visit-user':
                handleVisitUser(submission);
                break;
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1.2rem',
                            color: theme[themeStyle].primary_white,
                        }}>
                        Submissions
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {isMobile ? (
                            <>
                                <Tooltip title="Official Selection">
                                    <IconButton
                                        onClick={() => setOfficialSelectionOpen(true)}
                                        sx={{
                                            backgroundColor: theme[themeStyle].highlight,
                                            color: theme[themeStyle].black,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].highlight,
                                                opacity: 0.9,
                                            },
                                        }}>
                                        <SelectionIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Award Prizes">
                                    <IconButton
                                        onClick={() => setPrizeAwardModalOpen(true)}
                                        sx={{
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].black,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].primary_orange,
                                                opacity: 0.9,
                                            },
                                        }}>
                                        <AwardIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        canSendWinnerEmail
                                            ? 'Send Winners Email'
                                            : 'Complete all award assignments to enable this feature'
                                    }>
                                    <span>
                                        <IconButton
                                            onClick={handleSendWinnersEmail}
                                            disabled={!canSendWinnerEmail}
                                            sx={{
                                                backgroundColor: canSendWinnerEmail
                                                    ? theme[themeStyle].primary_orange
                                                    : theme[themeStyle].placeholderGray50,
                                                color: canSendWinnerEmail
                                                    ? theme[themeStyle].black
                                                    : theme[themeStyle].tag_white,
                                                '&:hover': canSendWinnerEmail
                                                    ? {
                                                          backgroundColor: theme[themeStyle].primary_orange,
                                                          opacity: 0.9,
                                                      }
                                                    : {},
                                                '&.Mui-disabled': {
                                                    backgroundColor: theme[themeStyle].placeholderGray50,
                                                    color: theme[themeStyle].tag_white,
                                                },
                                            }}>
                                            <EmailIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Export CSV">
                                    <IconButton
                                        onClick={() => onExportCSV(submissions)}
                                        sx={{
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].black,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].primary_orange,
                                                opacity: 0.9,
                                            },
                                        }}>
                                        <ExportIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            <>
                                <CustomButton
                                    onClick={() => setOfficialSelectionOpen(!officialSelectionOpen)} // This needs fixing to avoid double clicking
                                    label="Official Selection"
                                    customStyle={{
                                        borderRadius: '10px',
                                        backgroundColor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].black,
                                    }}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].highlight,
                                            opacity: 0.9,
                                        },
                                    }}
                                />
                                <CustomButton
                                    onClick={() => setPrizeAwardModalOpen(true)}
                                    label="Award Prizes"
                                    customStyle={{
                                        width: '120px',
                                        borderRadius: '10px',
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        color: theme[themeStyle].black,
                                    }}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            opacity: 0.9,
                                        },
                                    }}
                                />
                                <Tooltip
                                    title={
                                        canSendWinnerEmail
                                            ? 'Send Winners Email'
                                            : 'Complete all award assignments to enable this feature'
                                    }>
                                    <span>
                                        <CustomButton
                                            onClick={handleSendWinnersEmail}
                                            disabled={!canSendWinnerEmail}
                                            label="Send Winners Email"
                                            customStyle={{
                                                borderRadius: '10px',
                                                backgroundColor: canSendWinnerEmail
                                                    ? theme[themeStyle].primary_orange
                                                    : theme[themeStyle].placeholderGray50,
                                                color: canSendWinnerEmail
                                                    ? theme[themeStyle].black
                                                    : theme[themeStyle].tag_white,
                                            }}
                                        />
                                    </span>
                                </Tooltip>
                                <CustomButton
                                    onClick={() => onExportCSV(submissions)}
                                    label="Export CSV"
                                    customStyle={{
                                        width: '120px',
                                        borderRadius: '10px',
                                    }}
                                />
                            </>
                        )}
                    </Box>
                </Box>

                {/* Search and Filter Controls */}
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                placeholder="Search by title or username"
                                value={searchValue}
                                onChange={handleSearchInputChange}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: theme[themeStyle].tag_white }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchValue ? (
                                        <InputAdornment position="end">
                                            <CloseIcon
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    cursor: 'pointer',
                                                }}
                                                onClick={handleClearSearch}
                                            />
                                        </InputAdornment>
                                    ) : null,
                                    sx: {
                                        backgroundColor: theme[themeStyle].secondaryBg,
                                        borderRadius: 2,
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {contest?.categories_enabled && contest?.categories && contest.categories.length > 0 && (
                                <FormControl
                                    fullWidth
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            backgroundColor: theme[themeStyle].secondaryBg,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            borderRadius: 2,
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}>
                                    <InputLabel id="category-select-label" sx={{ color: theme[themeStyle].highlight }}>
                                        Category
                                    </InputLabel>
                                    <Select
                                        labelId="category-select-label"
                                        value={selectedCategory}
                                        onChange={handleCategoryChange}
                                        label="Category"
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}>
                                        <MenuItem value="all">All Categories</MenuItem>
                                        {contest.categories.map(category => (
                                            <MenuItem
                                                key={category._id || category.category_name}
                                                value={category.category_name}>
                                                {category.category_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <FormControl
                                    sx={{
                                        minWidth: 200,
                                        '& .MuiInputBase-root': {
                                            backgroundColor: theme[themeStyle].secondaryBg,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            borderRadius: 2,
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}>
                                    <InputLabel id="sort-select-label" sx={{ color: theme[themeStyle].highlight }}>
                                        Sort By
                                    </InputLabel>
                                    <Select
                                        labelId="sort-select-label"
                                        value={sortOption}
                                        onChange={handleSortChange}
                                        label="Sort By"
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <SortIcon sx={{ color: theme[themeStyle].tag_white }} />
                                            </InputAdornment>
                                        }>
                                        {SORT_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Box
                    sx={{
                        backgroundColor: theme[themeStyle].secondaryBg,
                        borderRadius: 2,
                        padding: 3,
                        '& .MuiDataGrid-root': {
                            backgroundColor: theme[themeStyle].secondaryBg,
                        },
                        '& .MuiDataGrid-filler': {
                            backgroundColor: theme[themeStyle].secondaryBg,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            backgroundColor: theme[themeStyle].secondaryBg,
                        },
                    }}>
                    <Table
                        columns={columnsData}
                        staticData={submissions}
                        rowsPerPageOptions={[5, 10, 20, 30]}
                        tableType="submissions"
                        rowHeight={90}
                        headerHeight={65}
                        textSx={{
                            fontSize: 14,
                        }}
                        sx={{
                            minHeight: 450,
                            backgroundColor: theme[themeStyle].secondaryBg,
                            '& .MuiDataGrid-root': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                            '& .MuiDataGrid-filler': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                        }}
                        fullscreenMoreIcon
                        fullscreenMoreMenu={moreMenuItems}
                        imageType="submissions"
                        onFullscreenMenuClick={handleOnMenuClicks}
                    />
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <CustomPagination
                            pagination={pagination}
                            onPageChange={handleSubmissionPageChange}
                            onRowsChange={handleSubmissionRowsChange}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Prize Award Modal */}
            {contest && awardCategories && (
                <PrizeAwardModal
                    open={prizeAwardModalOpen}
                    onClose={() => setPrizeAwardModalOpen(false)}
                    awardCategories={awardCategories.categories || []}
                    submissions={submissions}
                    contest={contest}
                    existingAwards={managedContest?.awards}
                    onAward={handlePrizeAward}
                />
            )}

            {/* Official Selection Menu */}
            {contestId && (
                <OfficialSelectionMenu
                    contestId={contestId}
                    open={officialSelectionOpen}
                    onClose={() => setOfficialSelectionOpen(false)}
                />
            )}

            {/* Send Winners Email Confirmation Dialog */}
            <DialogueSheet
                open={winnersEmailDialogOpen}
                onClose={handleCancelWinnersEmail}
                onAgreeClicked={() => handleConfirmedSendWinnersEmail(true)}
                onDisagreeClick={() => handleConfirmedSendWinnersEmail(false)}
                positiveText="Yes, Send As Test"
                negativeText="No, Send To Real Users"
                header="Send Winners Email?"
                body="You can test this email by first sending it to yourself, or send it directly to the winners."
            />
        </Box>
    );
};
